/**
 * Portuguese VAT/NIF validation.
 *
 * Logic ported verbatim from the invoice-express service
 * (libs/invoice-express/fiscal-invoicing/src/vat/vat-number.validator.ts):
 * a 9-digit number with a mod-11 check digit. Kept in sync manually because
 * this package (CRA) cannot import runtime code from outside its own `src`.
 * https://pt.wikipedia.org/wiki/N%C3%BAmero_de_identifica%C3%A7%C3%A3o_fiscal
 */

export type SupportedVatCountry = "PT";

const PT_NIF_LENGTH = 9;
const PT_CHECK_MODULO = 11;
const PT_MIN_CHECK_FOR_ZERO = 10;

const isValidPortugueseNif = (value: string): boolean => {
  if (!/^\d{9}$/.test(value)) {
    return false;
  }

  const digits = [...value].map(Number);

  // NIFs with a leading zero are not assigned by the Portuguese tax authority
  // (AT) and would be rejected by InvoiceExpress even though the checksum passes.
  if (digits[0] === 0) {
    return false;
  }

  let checksum = 0;
  for (let position = 0; position < PT_NIF_LENGTH - 1; position++) {
    checksum += digits[position] * (PT_NIF_LENGTH - position);
  }

  const remainder = checksum % PT_CHECK_MODULO;
  const expected = remainder < 2 ? 0 : PT_CHECK_MODULO - remainder;
  return (
    expected < PT_MIN_CHECK_FOR_ZERO && expected === digits[PT_NIF_LENGTH - 1]
  );
};

const VALIDATORS: Record<SupportedVatCountry, (value: string) => boolean> = {
  PT: isValidPortugueseNif,
};

/** Strips whitespace and an optional country prefix from a raw Portuguese VAT value. */
export const sanitizePortugueseVatNumber = (
  raw: string,
  country: SupportedVatCountry = "PT"
): string =>
  raw
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(new RegExp(`^${country}`), "");

export const isValidPortugueseVatNumber = (
  vatNumber: string,
  country: SupportedVatCountry = "PT"
): boolean => {
  const validator = VALIDATORS[country];
  return validator ? validator(vatNumber) : false;
};
